import { Link, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, ChevronRight } from 'lucide-react';
import { Cartao, Indicador } from '../../../web/src/componentes/base';
import { Estado } from '../../../web/src/componentes/estados';
import { dinheiro, textoLivre } from '../../../web/src/nucleo/formato';
import { descricaoGerada } from '../../../web/src/nucleo/texto-sistema';
import { pget } from '../api';
import { useSessao } from '../sessao';
import { dataCurta } from './comum';
import { SeloEstado } from './Financeiro';

/**
 * E1 (Relatórios, opção A): os relatórios que a equipe publicou para a empresa. Cada abertura recalcula com os dados do
 * momento (só as faturas da empresa da sessão; o servidor nunca recebe o cliente do navegador). Mesmos componentes do
 * Financeiro: cartão com lista, indicadores e a tabela dos relatórios do sistema.
 */

/** Espelho do resumo de `portal_relatorios` (`modules/portal/src/relatorios.ts`). */
interface Publicacao {
  id: string;
  tipo: 'posicao' | 'extrato';
  titulo: string;
  descricao: string;
  periodo: string | null;
  de: string | null;
  ate: string | null;
  base: 'competencia' | 'caixa';
  publicado_em: string;
}

/** Espelho de `LinhaRelatorioPortal` (`modules/contas/src/publico.ts`). */
interface Linha {
  id: string;
  numero: string;
  descricao: string;
  emissao: string;
  vencimento: string;
  estado: 'em_aberto' | 'parcial' | 'pago';
  vencida: boolean;
  dias_atraso: number;
  pago_em: string | null;
  moeda: string;
  valor_centavos: number;
  pago_centavos: number;
  saldo_centavos: number;
}

interface Total {
  moeda: string;
  documentos: number;
  valor_centavos: number;
  pago_centavos: number;
  saldo_centavos: number;
  vencido_centavos: number;
}

interface Resultado {
  relatorio: Publicacao;
  em: string;
  periodo: { de: string; ate: string } | null;
  linhas: Linha[];
  totais: Total[];
  truncado: boolean;
}

export function Relatorios() {
  const { t, i18n } = useTranslation('portal');
  const idioma = i18n.language.slice(0, 2);
  const s = useSessao();
  const q = useQuery({ queryKey: ['portal', 'relatorios'], queryFn: () => pget<{ itens: Publicacao[] }>('/relatorios') });
  return (
    <div className="flex flex-col gap-4">
      <h1 className="text-2xl font-bold tracking-tight">{t('relatorios.titulo')}</h1>
      <p className="text-sm text-tinta-3">{t('relatorios.texto')}</p>
      <Estado consulta={q}>
        {(d) => (
          <Cartao semPadding>
            {d.itens.length ? (
              <ul className="divide-y divide-borda">
                {d.itens.map((r) => (
                  <li key={r.id}>
                    <Link to={`/relatorios/${r.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-sup-2" data-relatorio={r.id}>
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-sm font-medium">{textoLivre(r.titulo, s.empresa.nome)}</p>
                        <p className="truncate text-xs text-tinta-3">
                          {t(`relatorios.tipo.${r.tipo}`)} · {t('relatorios.publicado', { data: dataCurta(r.publicado_em, idioma) })}
                        </p>
                      </div>
                      <ChevronRight className="size-4 shrink-0 text-tinta-3" aria-hidden />
                    </Link>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="p-4 text-sm text-tinta-3">{t('relatorios.vazio')}</p>
            )}
          </Cartao>
        )}
      </Estado>
    </div>
  );
}

export function RelatorioDetalhe() {
  const { t, i18n } = useTranslation('portal');
  const { id } = useParams<{ id: string }>();
  const idioma = i18n.language.slice(0, 2);
  const s = useSessao();
  const q = useQuery({ queryKey: ['portal', 'relatorio', id], queryFn: () => pget<Resultado>(`/relatorios/${id}`), enabled: !!id });
  return (
    <div className="flex flex-col gap-4">
      <Link to="/relatorios" className="flex w-fit items-center gap-1 text-sm text-tinta-2 hover:text-acento-forte">
        <ArrowLeft className="size-4" aria-hidden />
        {t('relatorios.voltar')}
      </Link>
      <Estado consulta={q}>
        {(d) => {
          const posicao = d.relatorio.tipo === 'posicao';
          return (
            <>
              <div className="flex min-w-0 flex-col gap-1">
                <h1 className="text-2xl font-bold tracking-tight break-words">{textoLivre(d.relatorio.titulo, s.empresa.nome)}</h1>
                <p className="text-sm text-tinta-3">
                  {posicao
                    ? t('relatorios.posicaoEm', { data: dataCurta(d.em, idioma) })
                    : t(d.relatorio.base === 'caixa' ? 'relatorios.pagasEntre' : 'relatorios.emitidasEntre', { de: dataCurta(d.periodo?.de, idioma), ate: dataCurta(d.periodo?.ate, idioma) })}
                </p>
                {d.relatorio.descricao && <p className="text-sm whitespace-pre-line text-tinta-2">{textoLivre(d.relatorio.descricao, s.empresa.nome)}</p>}
              </div>
              {d.totais.map((r) => (
                <section key={r.moeda} aria-label={t('relatorios.totais')} className="grid grid-cols-2 gap-3 md:grid-cols-4">
                  <Indicador rotulo={t('relatorios.documentos')} valor={String(r.documentos)} />
                  {posicao ? (
                    <>
                      <Indicador rotulo={t('financeiro.aberto')} valor={dinheiro(r.saldo_centavos, r.moeda)} />
                      <Indicador rotulo={t('financeiro.vencido')} valor={dinheiro(r.vencido_centavos, r.moeda)} cor={r.vencido_centavos ? 'perigo' : 'tinta-1'} />
                    </>
                  ) : (
                    <>
                      <Indicador rotulo={t('financeiro.valor')} valor={dinheiro(r.valor_centavos, r.moeda)} />
                      <Indicador rotulo={t('relatorios.pago')} valor={dinheiro(r.pago_centavos, r.moeda)} cor="verde" />
                      <Indicador rotulo={t('financeiro.aberto')} valor={dinheiro(r.saldo_centavos, r.moeda)} />
                    </>
                  )}
                </section>
              ))}
              <Cartao semPadding>
                {d.linhas.length ? (
                  <div tabIndex={0} role="region" aria-label={t('relatorios.tabela')} className="overflow-x-auto focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-foco">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b border-borda text-left text-tinta-3">
                          <th scope="col" className="px-3 py-2 font-medium">{t('relatorios.numero')}</th>
                          <th scope="col" className="px-3 py-2 font-medium">{t(posicao ? 'financeiro.vencimento' : d.relatorio.base === 'caixa' ? 'relatorios.pagoEm' : 'financeiro.emissao')}</th>
                          {posicao && <th scope="col" className="px-3 py-2 text-right font-medium">{t('relatorios.atraso')}</th>}
                          {!posicao && <th scope="col" className="px-3 py-2 text-right font-medium">{t('financeiro.valor')}</th>}
                          {!posicao && <th scope="col" className="px-3 py-2 text-right font-medium">{t('relatorios.pago')}</th>}
                          <th scope="col" className="px-3 py-2 text-right font-medium">{t('financeiro.saldo')}</th>
                          <th scope="col" className="px-3 py-2 font-medium">{t('relatorios.situacao')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {d.linhas.map((l) => (
                          <tr key={l.id} className="border-b border-borda last:border-0" data-linha={l.id}>
                            <td className="numero px-3 py-2 whitespace-nowrap">
                              <span className="block">{l.numero}</span>
                              {l.descricao && <span className="block max-w-64 truncate text-xs text-tinta-3">{textoLivre(descricaoGerada(l.descricao), s.empresa.nome)}</span>}
                            </td>
                            <td className={`numero px-3 py-2 whitespace-nowrap ${posicao && l.vencida ? 'text-perigo' : 'text-tinta-2'}`}>
                              {dataCurta(posicao ? l.vencimento : d.relatorio.base === 'caixa' ? l.pago_em : l.emissao, idioma)}
                            </td>
                            {posicao && <td className="numero px-3 py-2 text-right whitespace-nowrap">{l.dias_atraso ? t('relatorios.dias', { count: l.dias_atraso }) : t('relatorios.aVencer')}</td>}
                            {!posicao && <td className="numero px-3 py-2 text-right whitespace-nowrap">{dinheiro(l.valor_centavos, l.moeda)}</td>}
                            {!posicao && <td className="numero px-3 py-2 text-right whitespace-nowrap">{dinheiro(l.pago_centavos, l.moeda)}</td>}
                            <td className="numero px-3 py-2 text-right font-medium whitespace-nowrap">{dinheiro(l.estado === 'pago' ? 0 : l.saldo_centavos, l.moeda)}</td>
                            <td className="px-3 py-2">
                              <SeloEstado f={l} />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="p-4 text-sm text-tinta-3">{t(posicao ? 'financeiro.semAbertas' : 'relatorios.semFaturas')}</p>
                )}
              </Cartao>
              {d.truncado && <p className="text-xs text-tinta-3">{t('relatorios.truncado', { n: d.linhas.length })}</p>}
              <p className="text-xs text-tinta-3">{t('relatorios.atualizado')}</p>
            </>
          );
        }}
      </Estado>
    </div>
  );
}
